import { inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { TokenService } from './token.service';
export interface Gestoria {
  id: string;
  name: string;
  nif: string | null;
  direccion: string | null;
  email_contacto: string | null;
  phone: string | null;
  logo: string | null;
  activo: boolean;
  numero_empleados: number | null;
  numero_empleados_rango: string | null;
  sedes: string[];
  numero_sedes: number;
}

export type TipoEmpresa = 'autonomo' | 'micro_pyme' | 'mini_pyme' | 'pyme' | 'gran_empresa';

export interface Cliente {
  id: string;
  gestoria_id: string;
  nombre_empresa: string;
  nombre_responsable: string | null;
  apellido_responsable: string | null;
  nif: string;
  tipo_empresa: TipoEmpresa;
  actividad: string | null;
  direccion: string | null;
  telefono: string | null;
  email_contacto: string | null;
  numero_empleados: number;
  numero_empleados_rango: string | null;
  sedes: string[];
  numero_sedes: number;
  logotipo: string | null;
  logotipo_url: string | null;
  activo: boolean;
  created_at: string;
}

export type SimulationType =
  | 'super_admin_a_gestoria'
  | 'super_admin_a_empresa'
  | 'admin_a_empresa';

export interface SimulationBlock {
  simulacion_id: string;
  tipo: SimulationType;
  gestoria_id: string | null;
  cliente_id: string | null;
  user_simulado_id: string;
  iniciada_en?: string | null;
  expires_at: string;
  // Display helpers the backend may include
  gestoria_nombre?: string | null;
  cliente_nombre?: string | null;
}

export interface User {
  id: string;
  name: string;
  last_name: string | null;
  email: string;
  telefono: string | null;
  activo: boolean;
  gestoria_id: string | null;
  cliente_id: string | null;
  cliente_actual_id: string | null;
  es_principal: boolean | null;
  created_at: string;
  roles: Array<{ id: number; name: 'super_admin' | 'admin' | 'user' }>;
  gestoria: Gestoria | null;
  cliente: Cliente | null;
  simulando: SimulationBlock | null;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface RegisterRequest {
  invitation_token: string;
  name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface RegisterResponse {
  message: string;
  user: User;
}

export interface CompanyInvitationPreview {
  email: string;
  nombre: string | null;
  cliente: {
    id: string;
    nombre_empresa: string;
    nif: string | null;
  };
  gestoria: {
    id: string;
    name: string;
  };
  invitador: {
    name: string;
    email: string;
  } | null;
  mensaje_personalizado: string | null;
  expires_at: string | null;
}

export interface AcceptCompanyInvitationRequest {
  password: string;
  password_confirmation: string;
  acepta_terminos: boolean;
}

export interface AcceptCompanyInvitationResponse extends AuthResponse {
  message: string;
  acceso: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http         = inject(HttpClient);
  private readonly router       = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly apiUrl       = environment.apiUrl;

  private readonly _currentUser     = signal<User | null>(null);
  private readonly _isCheckingAuth  = signal<boolean>(true);

  public readonly currentUser      = this._currentUser.asReadonly();
  public readonly isCheckingAuth   = this._isCheckingAuth.asReadonly();
  public readonly isAuthenticated  = computed(() => this._currentUser() !== null);

  public updateCurrentUser(user: User): void {
    this._currentUser.set(user);
  }

  public updateCurrentUserCliente(cliente: Cliente | null): void {
    const user = this._currentUser();
    if (!user) return;
    this._currentUser.set({ ...user, cliente });
  }

  public updateCurrentUserGestoria(gestoria: Gestoria): void {
    const user = this._currentUser();
    if (!user) return;
    this._currentUser.set({ ...user, gestoria });
  }

  public getToken(): string | null {
    return this.tokenService.get();
  }

  public setToken(token: string, remember: boolean, expiresIn?: number): void {
    this.tokenService.set(token, remember, expiresIn);
  }

  public removeToken(): void {
    this.tokenService.remove();
  }

  public clearLocalSession(): void {
    this.removeToken();
    this._currentUser.set(null);
    this._isCheckingAuth.set(false);
  }

  public login(credentials: { email: string; password: string; remember: boolean }): Observable<boolean> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => this.setToken(response.access_token, credentials.remember, response.expires_in)),
      switchMap(() => this.checkAuth())
    );
  }

  public register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${this.apiUrl}/auth/register`, data);
  }

  public getCompanyInvitation(token: string): Observable<CompanyInvitationPreview> {
    const encodedToken = encodeURIComponent(token);
    return this.http.get<CompanyInvitationPreview>(`${this.apiUrl}/invitaciones/${encodedToken}`);
  }

  public acceptCompanyInvitation(
    token: string,
    data: AcceptCompanyInvitationRequest,
  ): Observable<AcceptCompanyInvitationResponse> {
    const encodedToken = encodeURIComponent(token);
    return this.http.post<AcceptCompanyInvitationResponse>(`${this.apiUrl}/invitaciones/${encodedToken}/aceptar`, data);
  }

  public getMicrosoftLoginUrl(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.apiUrl}/auth/microsoft/redirect`);
  }

  public forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/recuperar-password`, { email });
  }

  public resetPassword(data: { email: string; token: string; password: string; password_confirmation: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/auth/restablecer-password`, data);
  }

  public handleAuthResponse(response: Pick<AuthResponse, 'access_token' | 'expires_in'>): Observable<boolean> {
    const remember = !!localStorage.getItem('jwt_token');
    this.tokenService.set(response.access_token, remember, response.expires_in);
    return this.checkAuth();
  }

  public exitSimulation(): Observable<boolean> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/me/simulacion/finalizar`, {}).pipe(
      switchMap(response => {
        const remember = !!localStorage.getItem('jwt_token');
        this.tokenService.set(response.access_token, remember, response.expires_in);
        return this.checkAuth();
      }),
      catchError(() => of(false)),
    );
  }

  public logout(): Observable<any> {    const token = this.getToken();
    this.removeToken();
    this._currentUser.set(null);
    this.router.navigate(['/login']);

    if (token) {
      return this.http.post(`${this.apiUrl}/auth/logout`, {}).pipe(
        catchError(() => of(null))
      );
    }
    return of(null);
  }

  public checkAuth(): Observable<boolean> {
    const token = this.getToken();
    if (!token) {
      this._currentUser.set(null);
      this._isCheckingAuth.set(false);
      return of(false);
    }

    return this.http.get<User>(`${this.apiUrl}/auth/me`).pipe(
      map((user) => {
        this._currentUser.set(user);
        this._isCheckingAuth.set(false);
        return true;
      }),
      catchError(() => {
        this.removeToken();
        this._currentUser.set(null);
        this._isCheckingAuth.set(false);
        return of(false);
      })
    );
  }
}
